import React, { useState, useEffect, useRef } from 'react';
import { Modal, Box, Typography, Button, TextField, FormControl, Chip, Autocomplete, ListItemText, FormControlLabel, Checkbox, InputLabel, Select, MenuItem, Grid, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, IconButton, Snackbar } from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';

function EditShiftModal(props) {
  const [selectedCustomers,   setSelectedCustomers] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]); // State for selected employees
  const [startDate, setStartDate] = useState();
  const [startTime, setStartTime] = useState();
  const [endTime, setEndTime] = useState();
  const [scheduleName, setScheduleName] = useState('');
  const [notes, setNotes] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({
    selectedCustomers: false,
    selectedEmployees: false,
    startTime: false,
    endTime: false
  });
  const [customersOpen, setCustomersOpen] = useState(false);
  const [applyChangesToFuture, setApplyChangesToFuture] = useState(false);
  const [taskType, setTaskType] = useState(props.schedule?.task_type || '');
  const [maxCapacity, setMaxCapacity] = useState('');
  const [preAuthList, setPreAuthList] = useState([]); 
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [singleEmployeeOnly, setSingleEmployeeOnly] = useState(false);

  const initialDataRef = useRef(null);

  const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'success' });
  const showAlert = (message, severity) => {
    setAlertInfo({ open: true, message, severity });
    if (severity === 'success') props.onSuccess?.(message);
    else props.onError?.(message);
  };

  // const isAdmin = props.isAdmin; // Using passed prop to check if the user is an admin
  const entityLabelSingle = props.isDayCare ? 'Member' : 'Customer';
  const entityLabelPlural = props.isDayCare ? 'Members' : 'Customers';

  const assignAllOption = { cus_id: 'assign_all', cus_name: 'Assign All' };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) {
      return;
    }

    let scheduledSessions = props.sessions.filter(session => session.schedule_id === props.schedule.schedule_id);
    let scheduleCustomers = scheduledSessions.map(session => session.customer_id);

    if(selectedCustomers.length + scheduleCustomers.length > props.schedule.max_capacity) {
      showAlert(`The number of ${entityLabelPlural} assigned to the shift exceeds the maximum capacity`, 'error');
      return;
    }

    let sday = startDate ? startDate : props.schedule.date;
    let st = startTime ? startTime : props.schedule.start_time;
    let et = endTime ? endTime : props.schedule.end_time;

    let session = {
      schedule_id: props.schedule.schedule_id,
      customers: selectedCustomers.map(customer => customer.cus_id),
      date: sday,
      start_time: moment(st).date(moment(startDate).date()).utc().format('YYYY-MM-DD HH:mm:00'),
      end_time:  moment(et).date(moment(startDate).date()).utc().format('YYYY-MM-DD HH:mm:00'),
      notes: notes,
      reccurence: applyChangesToFuture
    }

    let schedule = { 
      date: sday,
      start_time: moment(st).date(moment(sday).date()).utc().format('YYYY-MM-DD HH:mm:00'),
      end_time: moment(et).date(moment(sday).date()).utc().format('YYYY-MM-DD HH:mm:00'),
      notes: sessionNotes,
      resource_id: props.schedule.resource_id,
      max_capacity: maxCapacity,
      schedule_name: scheduleName,
      task_type: taskType,
      reccurence: applyChangesToFuture
    };
    console.log(schedule, startTime, endTime);

    if(props.user.permissions.ScheduleSessionForOtherEmployees) {
      if(selectedEmployees.length > 0) {
        // Check single employee limit
        const totalEmployees = props.schedule.employee_ids.length + selectedEmployees.length;
        if (singleEmployeeOnly && totalEmployees > 1) {
          showAlert('This shift is limited to a single employee only', 'error');
          return;
        }
        try{
          await axios.post(APIURL + '/master_schedule/employee', { 
            schedule_id: props.schedule.schedule_id, 
            employee_ids: selectedEmployees.map(employee => employee.id), 
            reccurence: applyChangesToFuture,
            shift: singleEmployeeOnly
          });
          // Log "Employee Assigned" for each newly assigned employee
          selectedEmployees.forEach(emp => {
            axios.post(APIURL + '/shift_action_log', {
              schedule_id: props.schedule.schedule_id,
              shift_emp_id: emp.id,
              action: 'Employee Assigned',
              notes: null,
              action_by: props.user.emp_id,
              company_id: props.comp_id,
            }).catch(err => console.error('Error logging shift action:', err));
          });
          showAlert(`Employees/${entityLabelPlural} assigned to shift successfully`, 'success');
        } catch (error) {
          // Handle backend single employee enforcement error
          if (error.response && error.response.data && error.response.data.message) {
            showAlert(error.response.data.message, 'error');
          } else {
            showAlert(`An error occurred while assigning employees/${entityLabelPlural} to shift`, 'error');
          }
          return;
        }
      }
    }
    
    if(selectedCustomers.length > 0) {
      try {
        await axios.post(APIURL + '/sessions', session);
        // Log "Customer Assigned" for the shift
        axios.post(APIURL + '/shift_action_log', {
          schedule_id: props.schedule.schedule_id,
          shift_emp_id: null,
          action: 'Customer Assigned',
          notes: null,
          action_by: props.user.emp_id,
          company_id: props.comp_id,
        }).catch(err => console.error('Error logging shift action:', err));
        showAlert(`${entityLabelPlural} assigned to shift successfully`, 'success');
      } catch (error) {
        console.log(error);
          if(error.response) {
            showAlert(error.response.data.message, 'error');
          } else {
            showAlert(`An error occurred while assigning ${entityLabelPlural} to shift`, 'error');
          }
          return;
      }
    } 

    if(startDate !== props.schedule.date || startTime !== props.schedule.start_time || endTime !== props.schedule.end_time || scheduleName !== (props.schedule.schedule_name || '')) {
      try {
        await axios.put(APIURL + '/master_schedule/' + props.schedule.schedule_id, schedule);
        // Log "Shift Edited" when time/date is updated
        axios.post(APIURL + '/shift_action_log', {
          schedule_id: props.schedule.schedule_id,
          shift_emp_id: null,
          action: 'Shift Edited',
          notes: null,
          action_by: props.user.emp_id,
          company_id: props.comp_id,
        }).catch(err => console.error('Error logging shift action:', err));
        showAlert("Schedule updated successfully", 'success');
      } catch (error) {
        if(error.response) {
          showAlert(error.response.data.message, 'error');
        } else {
          showAlert("An error occurred while updating schedule", 'error');
        }
        return;
      }
    }

    props.reloadScheduleData();
    props.onClose();
  };

  const handleDeleteEmployee = async (employee) => {
    try {

      await axios.delete(APIURL + '/master_schedule/employee/' + employee.id, { data: { schedule_id: props.schedule.schedule_id, reccurence: applyChangesToFuture } });
      // Log "Employee Unassigned"
      axios.post(APIURL + '/shift_action_log', {
        schedule_id: props.schedule.schedule_id,
        shift_emp_id: employee.id,
        action: 'Employee Unassigned',
        notes: null,
        action_by: props.user.emp_id,
        company_id: props.comp_id,
      }).catch(err => console.error('Error logging shift action:', err));
      props.reloadScheduleData();
      showAlert("Employee removed from shift successfully", 'success');
    } catch (error) {
      console.log(error);
      showAlert("An error occurred while removing employee from session", 'error');
    }   
  };

  const handleDeleteCustomer = async (customer) => {
    try {
      const session = props.sessions.find(s => s.schedule_id === props.schedule.schedule_id && s.customer_id === customer.cus_id);
      if (session.task_id && session.check_in_time !== null) {
        await axios.put(APIURL + '/sessions/attendance/' + session.session_id, {
          tas_id: session.task_id,
          tas_is_active: 0,
          tas_type: null,
          tas_start_time: null,
          tas_end_time: null,
          check_in_time: null,
          check_out_time: null,
          task_id: session.task_id,
          cus_id: session.customer_id,
          emp_id: props.user.emp_id,
          company_id: props.user.comp_id
        });
      }
      // delete the session
      await axios.delete(APIURL + '/sessions/' + session.session_id, { data: { reccurence: applyChangesToFuture } });
      // Log "Customer Unassigned"
      axios.post(APIURL + '/shift_action_log', {
        schedule_id: props.schedule.schedule_id,
        shift_emp_id: null,
        action: 'Customer Unassigned',
        notes: null,
        action_by: props.user.emp_id,
        company_id: props.comp_id,
      }).catch(err => console.error('Error logging shift action:', err));
      props.reloadSessionData();
      showAlert(`${entityLabelSingle} removed from session successfully`, 'success');
    } catch (error) {      
      showAlert(`An error occurred while removing ${entityLabelSingle} from session`, 'error');
    }   
  };  

  const validate = () => {
    const nameChanged = scheduleName !== (props.schedule?.schedule_name || '');
    const scheduleChanged = nameChanged || startDate !== props.schedule.date || startTime !== props.schedule.start_time || endTime !== props.schedule.end_time;
    let errors;
    if(props.user.permissions.ScheduleSessionForOtherEmployees) {
      errors = {
        selectedCustomers: selectedCustomers.length === 0 && selectedEmployees.length === 0 && !scheduleChanged,
        selectedEmployees: selectedEmployees.length === 0 && selectedCustomers.length === 0 && !scheduleChanged,
        startTime: !startTime || moment(startTime).isSameOrAfter(moment(endTime)),
        endTime: !endTime || moment(endTime).isSameOrBefore(moment(startTime))
      };
    } else {
      errors = {
        selectedCustomers: selectedCustomers.length === 0 && !scheduleChanged,
        startTime: !startTime || moment(startTime).isSameOrAfter(moment(endTime)),
        endTime: !endTime || moment(endTime).isSameOrBefore(moment(startTime))
      };
    }
    console.log(errors);
    setErrors(errors);
    setSubmitted(true);
    return !Object.values(errors).some(err => err);
  };

  const unassignedCustomers = () => {
    let scheduledSessions = props.sessions.filter(session => session.schedule_id === props.schedule?.schedule_id);
    let scheduleCustomers = scheduledSessions.map(session => session.customer_id);
    return props.customers.filter(customer => !scheduleCustomers.includes(customer.cus_id));
  }

  const assginedCustomers = () => {
    let scheduledSessions = props.sessions.filter(session => session.schedule_id === props.schedule?.schedule_id);
    let scheduleCustomers = scheduledSessions.map(session => session.customer_id);
    return props.customers.filter(customer => scheduleCustomers.includes(customer.cus_id));
  }

  const unassginedEmployees = () => {
    return props.schedule?.employee_ids ? props.employees.filter(employee => !props.schedule.employee_ids.includes(employee.id)) : props.employees;
  }

  const assginedEmployees = () => {
    return props.schedule?.employee_ids ? props.employees.filter(employee => props.schedule.employee_ids.includes(employee.id)) : [];
  }

  useEffect(() => {
    if (!props.open) {
      setSubmitted(false);
      setErrors({
        selectedCustomers: false,
        selectedEmployees: false,
      });
      setSelectedCustomers([]);
      setSelectedEmployees([]);
      setNotes('');
    } else { 
      // Store date as plain string (YYYY-MM-DD) to avoid timezone comparison issues
      setStartDate(props.schedule?.date ? props.schedule?.date.substring(0, 10) : '');
      setStartTime(props.schedule?.start_time);
      setEndTime(props.schedule?.end_time);
      setSessionNotes(props.schedule?.notes || '');
      setScheduleName(props.schedule?.schedule_name || '');
      setTaskType(props.schedule?.task_type || '');
      setMaxCapacity(props.schedule?.max_capacity); // set maxCapacity on open
      setSingleEmployeeOnly(true); // Default to true for shift components
      // Initialize snapshot for dirty check
      initialDataRef.current = {
        startDate: props.schedule?.date ? props.schedule?.date.substring(0, 10) : '',
        startTime: props.schedule?.start_time ? moment(props.schedule?.start_time).format('HH:mm') : '',
        endTime: props.schedule?.end_time ? moment(props.schedule?.end_time).format('HH:mm') : '',
        sessionNotes: props.schedule?.notes || '',
        scheduleName: props.schedule?.schedule_name || '',
        taskType: props.schedule?.task_type || '',
        maxCapacity: String(props.schedule?.max_capacity || ''),
      };
    }
  }, [props.open]);

  useEffect(() => {
    if(props.isDayCare && taskType) {
      axios
      .get(APIURL + "/preAuth/taskType", {
              params: { 
                companyID: props.comp_id, 
                fromDate: moment(startDate).isValid() ? moment(startDate).subtract(2, 'months').format('MM/DD/YYYY') : null,
                toDate: moment(startDate).isValid() ? moment(startDate).endOf("day").format('MM/DD/YYYY') : null,
                taskType
              },
            })
        .then(response => setPreAuthList(response.data.sort((a, b) => new Date(b.startDate) - new Date(a.startDate))))
        .catch(error => console.error(error));
    } else {
      setPreAuthList([]);
    }
  }, [taskType, props.isDayCare]);

  const isDirty = () => {
    const snap = initialDataRef.current;
    if (!snap) return false;
    const current = {
      startDate: startDate || snap.startDate,
      startTime: startTime ? moment(startTime).format('HH:mm') : snap.startTime,
      endTime: endTime ? moment(endTime).format('HH:mm') : snap.endTime,
      sessionNotes: sessionNotes || '',
      scheduleName: scheduleName || '',
      taskType: taskType || '',
      maxCapacity: String(maxCapacity || ''),
    };
    if (selectedCustomers.length > 0) return true;
    if (selectedEmployees.length > 0) return true;
    return (
      current.startDate !== snap.startDate ||
      current.startTime !== snap.startTime ||
      current.endTime !== snap.endTime ||
      current.sessionNotes !== snap.sessionNotes ||
      current.scheduleName !== snap.scheduleName ||
      current.taskType !== snap.taskType ||
      current.maxCapacity !== snap.maxCapacity
    );
  };

  const handleAttemptClose = (event, reason) => {
    if (isDirty()) {
      setShowConfirmClose(true);
    } else {
      props.onClose();
    }
  };

  const handleDiscardChanges = () => {
    setShowConfirmClose(false);
    props.onClose();
  };

  return (
    <>
    <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={alertInfo.open} autoHideDuration={6000} onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} sx={{ zIndex: 99999 }}>
      <MuiAlert onClose={() => setAlertInfo(prev => ({ ...prev, open: false }))} severity={alertInfo.severity}>{alertInfo.message}</MuiAlert>
    </Snackbar>
    <Modal enforceFocus={false}
      open={props.open}
      // onClose={handleAttemptClose}
      aria-labelledby="assign-entity-modal-title"
      aria-describedby="assign-entity-modal-description"
    >
      <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 500, maxWidth: '90%', bgcolor: 'background.paper', boxShadow: 24, p: 4, borderRadius: 2, maxHeight: '95vh', overflowY: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography id="assign-entity-modal-title" variant="h6" component="div" sx={{ mb: 1 }}>
            Edit Shift
          </Typography>
          <IconButton
            aria-label="close"
            color="inherit"
            sx={{ p: 0.25 }}
            onClick={handleAttemptClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <TextField
          label="Shift Name"
          fullWidth
          value={scheduleName}
          onChange={(e) => setScheduleName(e.target.value)}
          inputProps={{ maxLength: 50 }}
          sx={{ mb: 1, mt: 0.5 }}
        />
        <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
          {props.schedule?.task_type && <span>{props.schedule?.task_type} </span>}
          {props.resources.find(resource => resource.resource_id === props.schedule?.resource_id)?.resource_name} {moment(props.schedule?.date).utc().format('MM/DD/YYYY')} {moment(props.schedule?.start_time).format('hh:mm A')} - {moment(props.schedule?.end_time).format('hh:mm A')}
        </Typography>
        {props.user.permissions.ScheduleSessionForOtherEmployees && (
          <>
            <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
              Assigned Employees:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {assginedEmployees().map(employee => (
                <Chip key={employee.id} label={employee.employee_name} color="primary" sx={{ mb: .15}} onDelete={() => handleDeleteEmployee(employee)} />
              ))}
            </Box>
            <FormControl fullWidth margin="normal" sx={{ mt: 1, mb: 1 }}>
              <Autocomplete
                multiple={!singleEmployeeOnly}
                id="employee-select"
                options={unassginedEmployees()}
                getOptionLabel={(option) => option.employee_name}
                value={singleEmployeeOnly ? (selectedEmployees[0] || null) : selectedEmployees}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label={singleEmployeeOnly ? "Select Employee" : "Select Employees"}
                    placeholder={singleEmployeeOnly ? "Employee" : "Employees"}
                    error={errors.selectedEmployees && submitted}
                  />
                )}
                onChange={(event, newValue) => {
                  // Prevent assignment if single employee only and one is already assigned
                  if (singleEmployeeOnly && assginedEmployees().length > 0) {
                    showAlert('This shift is limited to a single employee only', 'error');
                    return;
                  }
                  // Handle single vs multiple employee selection
                  if (singleEmployeeOnly) {
                    setSelectedEmployees(newValue ? [newValue] : []);
                  } else {
                    setSelectedEmployees(newValue);
                  }
                }}
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.id}>
                      <ListItemText primary={option.employee_name} />
                    </li>
                  )
                }}
              />
            </FormControl>
          </>
        )}

        <Typography variant="subtitle2" component="div" sx={{ mb: 1 }}>
          Assigned {entityLabelPlural}:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {assginedCustomers().map(customer => (
            <Chip key={customer.cus_id} label={customer.cus_name} color="primary" sx={{ mb: .15}} onDelete={() => handleDeleteCustomer(customer)} />
          ))}
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <FormControl fullWidth margin="normal" sx={{ mt: 1, mb: 1 }}>
            <Autocomplete
              multiple
              id="customer-select"
              options={[assignAllOption, ...unassignedCustomers()]}
              value={selectedCustomers} // controlled value
              getOptionLabel={(option) => option.cus_name}
              filterSelectedOptions
              renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={`Select ${entityLabelPlural}`}
                    placeholder={entityLabelPlural}
                    error={errors.selectedCustomers && submitted}
                />
              )}
              open={customersOpen}
              onOpen={() => {
                setCustomersOpen(true);
              }}
              onClose={(event, reason) => {
                if (reason === 'selectOption' && event.target.innerText !== 'Select All') {
                  setCustomersOpen(true);
                } else {
                  setCustomersOpen(false);
                }
              }}
              onChange={(event, newValue) => {
                if (newValue.some(item => item.cus_id === 'assign_all')) {
                  setSelectedCustomers(unassignedCustomers());
                } else {
                  setSelectedCustomers(newValue.filter(item => item.cus_id !== 'assign_all'));
                }
              }}
              renderOption={(props, option) => {
                const preAuth = preAuthList.find(item => item.cus_id === option.cus_id);
                const remainingUnits = preAuth ? Math.max(0, preAuth.remainingUnits) : null;
                const unitString = remainingUnits !== null ?
                  `${remainingUnits}${remainingUnits === 1 ? ' Unit' : ' Units'}` : '';
                 return (
                  <li {...props} key={option.cus_id}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      {option.cus_id === 'assign_all' ? (
                        <span style={{ color: '#3fa7d6', cursor: 'pointer' }}>Select All</span>
                      ) : (
                        <>
                          <span>{option.cus_name}</span>
                          {option.cus_status === 'Prospect' && (
                            <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'gray' }}>Prospect</span>
                          )}
                          {unitString && <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'gray' }}>{unitString}</span>}
                        </>
                      )}
                    </div>
                  </li>
                )
              }}
            />
          </FormControl>
          <Grid container spacing={2} alignItems="center" sx={{ mt: 0, mb: 2 }}>
            <Grid size={8}>
              <FormControl fullWidth>
                <InputLabel id="task-type-label">Task Type</InputLabel>
                <Select
                  labelId="task-type-label"
                  id="task-type-select"
                  value={taskType}
                  label="Task Type"
                  onChange={(e) => setTaskType(e.target.value)}
                >
                  {props.taskTypes.map((taskType) => (
                    <MenuItem key={taskType} value={taskType}>
                      {taskType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={4}>
              <TextField
                label="Max Capacity"
                type="number"
                value={maxCapacity}
                onChange={(e) => setMaxCapacity(e.target.value)}
                fullWidth
                inputProps={{
                  min: 1,
                  max: (props.resources?.find(r => r.resource_id === props.schedule?.resource_id)?.capacity) || ''
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex ', flexDirection: 'row', gap: 1, mb: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Date"
                value={startDate ? moment(startDate).toDate() : null}
                sx={{ width: '36%' }}
                onChange={(newValue) => { 
                  if (newValue && moment(newValue).isValid()) {
                    setStartDate(moment(newValue).format('YYYY-MM-DD'));
                  } else {
                    setStartDate('');
                  }
                }}
              />
              <TimePicker
                label="Start Time"
                value={new Date(startTime)}
                slotProps={{ textField: { error: errors.startTime && submitted } }}
                sx={{ width: '32%' }}
                onChange={(newValue) => setStartTime(newValue)}
              />
              <TimePicker
                label="End Time"
                value={new Date(endTime)}
                slotProps={{ textField: { error: errors.endTime && submitted } }}
                sx={{ width: '32%' }}
                onChange={(newValue) => setEndTime(newValue)}
              />
            </LocalizationProvider>
          </Box>
          <TextField
            margin="normal"
            fullWidth
            label="Session Notes"
            multiline
            rows={4}
            value={sessionNotes}
            onChange={(e) => setSessionNotes(e.target.value)}
            sx={{ mb: 1 }}
            inputProps={{ maxLength: 2000 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={applyChangesToFuture}
                onChange={(e) => setApplyChangesToFuture(e.target.checked)}
              />
            }
            label="Apply changes to future sessions"
          />
{/*           <TextField
            margin="normal"
            fullWidth
            label="Customer Notes"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          /> */}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Button type="submit" fullWidth variant="contained">
              Edit Shift
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => { props.onDuplicate?.(); }}
            >
              Duplicate
            </Button>
          </Box>
        </Box>
      </Box>
  </Modal>
  <Dialog open={showConfirmClose} onClose={() => setShowConfirmClose(false)}>
      <DialogTitle>Discard changes?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You have unsaved changes. Are you sure you want to close without saving?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowConfirmClose(false)}>Continue Editing</Button>
        <Button color="error" onClick={handleDiscardChanges} autoFocus>Discard</Button>
      </DialogActions>
    </Dialog>
  </>
  );
}

export default EditShiftModal;
